import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import { Box, ClickAwayListener, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { SkillAudience } from "generated-graphql/graphql";
import { useLayoutEffect, useRef, type ReactNode, type RefObject } from "react";

import {
  SKILL_AUDIENCE_TOKENS,
  SKILL_OWNER_TOKENS,
  skillAccessCaption,
  skillAccessTint,
} from "../../SkillAccess/skillAccessTokens";
import { type ScoutSkillMenuItem, type SkillMenuSections } from "./skillMenuModel";
import { SkillOwnerIcon } from "./SkillOwnerIcon";

const LISTBOX_ID = "scout-skill-slash-listbox";

export function scoutSkillOptionId(index: number): string {
  return `scout-skill-option-${index}`;
}

type ScoutSkillSlashMenuProps = {
  open: boolean;
  sections: SkillMenuSections;
  /** Text typed after `/`; filters the list and hides Recent. */
  query: string;
  highlightedIndex: number;
  onHighlight: (index: number) => void;
  onSelect: (item: ScoutSkillMenuItem) => void;
  /** Shows a Manage Skills button in the description footer when provided. */
  onManageSkills?: () => void;
  /** Called for a click outside the menu and its composer. */
  onClose: () => void;
  /** The composer; clicks there move the caret, which re-checks the query instead. */
  anchorRef: RefObject<HTMLElement | null>;
};

/** Keep focus in the composer textarea: a click there would blur and close the menu. */
function keepComposerFocus(event: React.MouseEvent) {
  event.preventDefault();
}

function ManageSkillsButton({ onManageSkills }: { onManageSkills: () => void }) {
  return (
    <Tooltip title="Manage Skills">
      <IconButton
        size="small"
        aria-label="Manage Skills"
        onMouseDown={keepComposerFocus}
        onClick={onManageSkills}
      >
        <SettingsOutlined fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

export function ScoutSkillSlashMenu({
  open,
  sections,
  query,
  highlightedIndex,
  onHighlight,
  onSelect,
  onManageSkills,
  onClose,
  anchorRef,
}: ScoutSkillSlashMenuProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the keyboard-highlighted row visible inside the scrolling list.
  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    const option = listRef.current?.querySelector<HTMLElement>(
      `#${scoutSkillOptionId(highlightedIndex)}`
    );
    option?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, open]);

  if (!open) {
    return null;
  }

  const { recent, rest, flat, sharedSlugs } = sections;
  const hasQuery = query.trim().length > 0;
  const highlighted = flat[Math.min(highlightedIndex, flat.length - 1)];

  const renderRow = (item: ScoutSkillMenuItem, index: number) => (
    <SkillRow
      key={item.id}
      item={item}
      showAccess={sharedSlugs.has(item.slug)}
      index={index}
      selected={index === highlightedIndex}
      onHighlight={onHighlight}
      onSelect={onSelect}
    />
  );

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const anchor = anchorRef.current;
    if (anchor && event.target instanceof Node && anchor.contains(event.target)) {
      return;
    }
    onClose();
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Paper
        elevation={8}
        // Any press inside the menu (rows, padding, scrollbar) keeps the composer focused.
        onMouseDown={keepComposerFocus}
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "100%",
          mb: 1,
          borderRadius: 2,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          zIndex: (theme) => theme.zIndex.modal,
        }}
      >
        <Box
          ref={listRef}
          id={LISTBOX_ID}
          role="listbox"
          aria-label="Skills"
          sx={{ maxHeight: 300, overflow: "auto", pb: 0.5 }}
        >
          {flat.length === 0 ? (
            <Box sx={{ px: 1.5, py: 1.25 }}>
              <Typography variant="body2" component="span">
                {hasQuery ? `No skills match “${query.trim()}”` : "No skills here yet"}
              </Typography>
            </Box>
          ) : (
            <>
              {recent.length > 0 ? (
                <>
                  <SectionHeading>Recent</SectionHeading>
                  {recent.map((item, i) => renderRow(item, i))}
                </>
              ) : null}
              {rest.length > 0 ? (
                <>
                  {recent.length > 0 ? <SectionDivider /> : null}
                  {rest.map((item, i) => renderRow(item, i + recent.length))}
                </>
              ) : null}
            </>
          )}
        </Box>

        {highlighted ? (
          <SkillDetails item={highlighted} onManageSkills={onManageSkills} />
        ) : onManageSkills ? (
          <Box
            sx={{
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "action.hover",
              display: "flex",
              justifyContent: "flex-end",
              px: 1,
              py: 0.5,
            }}
          >
            <ManageSkillsButton onManageSkills={onManageSkills} />
          </Box>
        ) : null}
      </Paper>
    </ClickAwayListener>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        px: 1.5,
        pt: 1,
        pb: 0.25,
        fontSize: 11,
        letterSpacing: "0.08333em",
        textTransform: "uppercase",
        color: "text.secondary",
      }}
    >
      {children}
    </Box>
  );
}

/** Thin rule between Recent and the rest of the list (replaces an "All Skills" label). */
function SectionDivider() {
  return (
    <Box
      aria-hidden
      sx={{
        mx: 1.5,
        my: 0.5,
        borderTop: 1,
        borderColor: "divider",
      }}
    />
  );
}

function SkillRow({
  item,
  showAccess,
  index,
  selected,
  onHighlight,
  onSelect,
}: {
  item: ScoutSkillMenuItem;
  /** Another skill shares this slug: say where this one activates and who sees it. */
  showAccess: boolean;
  index: number;
  selected: boolean;
  onHighlight: (index: number) => void;
  onSelect: (item: ScoutSkillMenuItem) => void;
}) {
  return (
    <Box
      id={scoutSkillOptionId(index)}
      role="option"
      aria-selected={selected}
      onMouseEnter={() => onHighlight(index)}
      onMouseDown={(event) => {
        keepComposerFocus(event);
        onSelect(item);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        height: 28,
        px: 1.5,
        cursor: "pointer",
        bgcolor: selected ? "action.selected" : "transparent",
      }}
    >
      <Tooltip title={SKILL_OWNER_TOKENS[item.owner].label} placement="left" disableInteractive>
        <Box
          component="span"
          sx={{ display: "flex", color: "action.active" }}
          aria-label={SKILL_OWNER_TOKENS[item.owner].label}
          role="img"
        >
          <SkillOwnerIcon owner={item.owner} size={16} />
        </Box>
      </Tooltip>
      <Typography variant="body2" sx={{ flexShrink: 0, fontWeight: 500 }} noWrap>
        {item.displayName}
      </Typography>
      {showAccess ? (
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }} noWrap>
          {skillAccessCaption(item)}
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
        {item.description}
      </Typography>
      {item.audience === SkillAudience.Staff ? <StaffChip /> : null}
    </Box>
  );
}

function StaffChip() {
  return (
    <Box
      component="span"
      sx={(theme: Theme) => ({
        flexShrink: 0,
        fontSize: 11,
        lineHeight: "18px",
        height: 18,
        px: 0.75,
        borderRadius: "9px",
        ...skillAccessTint(theme, SKILL_AUDIENCE_TOKENS[SkillAudience.Staff].color),
      })}
    >
      Staff
    </Box>
  );
}

/** Description area: always reserves two lines so the strip doesn't jump. */
const DETAILS_DESC_LINE_HEIGHT_PX = 19.5; // 13px * 1.5
const DETAILS_DESC_HEIGHT_PX = DETAILS_DESC_LINE_HEIGHT_PX * 2;

/**
 * Meta + description for the highlighted skill. Height is fixed to two
 * description lines so switching skills doesn't resize the menu.
 */
function SkillDetails({
  item,
  onManageSkills,
}: {
  item: ScoutSkillMenuItem;
  onManageSkills?: () => void;
}) {
  return (
    <Box
      aria-live="polite"
      sx={{
        borderTop: 1,
        borderColor: "divider",
        bgcolor: "action.hover",
        px: 1.5,
        pt: 1,
        pb: 1.25,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <Typography variant="caption" color="primary" noWrap sx={{ flexShrink: 0 }}>
          /{item.slug}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: "text.secondary",
            minWidth: 0,
            flexGrow: 1,
          }}
        >
          <SkillOwnerIcon owner={item.owner} size={14} />
          <Typography variant="caption" noWrap>
            {skillAccessCaption(item)}
          </Typography>
        </Box>
        {onManageSkills ? <ManageSkillsButton onManageSkills={onManageSkills} /> : null}
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontSize: 13,
          lineHeight: `${DETAILS_DESC_LINE_HEIGHT_PX}px`,
          height: DETAILS_DESC_HEIGHT_PX,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {item.description}
      </Typography>
    </Box>
  );
}
